import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideNoticeAdminManage: React.FC = () => {
  return (
    <GuidePageLayout
      title="通知を管理する（確認・編集・削除・解決・検索）"
      description="通知の確認、修正、削除、一括解決、検索を行います。"
      breadcrumbs={[{ label: '通知管理', path: '/guide/notice/overview' }]}
    >
      {/* 画面モード */}
      <GuideSection title="画面モード">
        <GuideTable
          headers={['モード', 'タイトル', '説明']}
          rows={[
            [
              '参照モード',
              '通知のタイトル',
              '通知内容を参照専用で表示。編集ボタンで編集モードに切替（編集可能な場合）',
            ],
            [
              '編集モード',
              'タイトル（編集中）',
              '各フィールドが入力可能になる。戻る矢印で参照モードに戻る',
            ],
          ]}
        />
      </GuideSection>

      {/* 詳細表示 */}
      <GuideSection title="通知詳細を確認する">
        <GuideStepList
          steps={[
            { label: '通知管理画面で対象の通知を検索・選択する' },
            {
              label: '参照モード（通知詳細）が表示される',
              description: 'ヘッダーに通知のタイトルが表示されます。戻る矢印で一覧に戻ります。',
              content: (
                <GuideMockContainer label="参照モード（ヘッダー部分）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        通知タイトル
                      </Typography>
                      <GuideMockChip label="未解決" color="warning" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5 }}>
                      作成日時: 2025/01/15 10:30
                    </Typography>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '参照モードのフッターに表示されるボタン',
              content: (
                <GuideMockContainer label="参照モード フッター（編集可能な場合）">
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <GuideMockButton label="削除" color="error" startIcon={<DeleteIcon />} />
                    <GuideMockButton
                      label="解決"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                    />
                    <GuideMockButton label="編集" startIcon={<EditIcon />} />
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      {/* 編集 */}
      <GuideSection title="通知の内容を修正する（BUC-N03）">
        <GuideStepList
          steps={[
            { label: '通知管理画面で対象の通知を検索・選択する' },
            {
              label: '「編集」ボタンを押下して編集モードに切り替える',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton label="編集" startIcon={<EditIcon />} />
                </GuideMockContainer>
              ),
            },
            {
              label: '編集モードで内容を修正する',
              content: (
                <GuideMockContainer label="編集モード（ヘッダー部分）">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ pointerEvents: 'none' }}
                  >
                    <IconButton size="small">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" fontWeight={700}>
                      通知タイトル（編集中）
                    </Typography>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「更新」ボタンを押下する',
              content: (
                <GuideMockContainer label="編集モード フッター">
                  <GuideMockButton label="更新" startIcon={<SaveIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox type="warning">
            システム通知は編集できません（編集ボタンが非表示）。
          </GuideInfoBox>
          <GuideInfoBox type="warning">解決済みの通知は編集できません。</GuideInfoBox>
          <GuideInfoBox>通知種別と対象ユーザーは変更できません。</GuideInfoBox>
          <GuideInfoBox>
            編集モードで戻る矢印をクリックすると、未保存の変更は破棄されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 削除 */}
      <GuideSection title="通知を削除する（BUC-N04）">
        <GuideStepList
          steps={[
            { label: '通知管理画面で対象の通知を検索・選択する' },
            {
              label: '参照モードのフッターにある「削除」ボタンを押下する',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton label="削除" color="error" startIcon={<DeleteIcon />} />
                </GuideMockContainer>
              ),
            },
            { label: '削除確認ダイアログで「実行」ボタンを押下する' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox type="warning">
            削除は取り消せません。削除された通知は復元できません。
          </GuideInfoBox>
          <GuideInfoBox>システム通知は削除できません（削除ボタンが非表示）。</GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 一括解決 */}
      <GuideSection title="お知らせを一括解決する（BUC-N05）">
        <GuideStepList
          steps={[
            { label: '通知管理画面で対象のお知らせ/重要なお知らせを検索・選択する' },
            {
              label: '参照モードのフッターにある「解決」ボタンを押下する',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton
                    label="解決"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                  />
                </GuideMockContainer>
              ),
            },
            { label: '確認ダイアログで「解決する」を選択する' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox type="warning">
            一括解決は取り消せません。全ユーザーに対して解決済みとなります。
          </GuideInfoBox>
          <GuideInfoBox type="warning">解決後は通知内容の修正もできなくなります。</GuideInfoBox>
          <GuideInfoBox>NOTICE種別には一括解決は使えません。</GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* ボタンと操作 */}
      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', 'モード', '動作']}
          rows={[
            ['← 戻る矢印（参照モード）', '参照', '詳細画面を閉じて一覧に戻る'],
            ['削除ボタン', '参照', '通知を削除する（システム通知以外）'],
            ['解決ボタン', '参照', '通知を一括解決する（お知らせ/重要のみ・未解決時のみ表示）'],
            ['編集ボタン', '参照', '編集モードに切り替える（編集可能な場合のみ表示）'],
            ['← 戻る矢印（編集モード）', '編集', '編集内容を破棄して参照モードに戻る'],
            ['更新ボタン', '編集', '入力内容で通知を更新する'],
          ]}
        />
      </GuideSection>

      {/* 検索 */}
      <GuideSection title="通知を検索する（BUC-N06）">
        <GuideStepList
          steps={[
            { label: '通知管理画面を開く' },
            {
              label: '検索条件を入力する',
              content: (
                <GuideMockContainer label="検索条件チップ">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <GuideMockChip label="ユーザー: user01" color="info" />
                    <GuideMockChip label="タイトル: 重要" color="info" />
                    <GuideMockChip label="期限あり" color="warning" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「検索」ボタンを押下する' },
          ]}
        />
        <GuideTable
          headers={['項目名', '形式', '備考']}
          rows={[
            ['ユーザー', 'プルダウン', '特定ユーザー宛ての通知（NOTICEタブ）'],
            ['タイトル', 'テキスト', 'キーワード検索'],
            ['期限あり', 'チェックボックス', '期限付き通知のみ'],
            ['作成日FROM/TO', '日付', '期間指定'],
          ]}
        />
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideNoticeAdminManage;
